import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { testimonials as fallbackTestimonials } from "../../data/homeLuxury";
import { getSiteReviews } from "../../services/orderPortal";
import { fetchReviewsFromSheet } from "../../utils/sheetReviews";

const AUTO = 6500;

function mapApiReview(row, idx) {
  return {
    id: `${row.timestamp || idx}-${row.reviewer || idx}`,
    quote: row.comment,
    name: row.reviewer || "Customer",
    role: row.rating ? `${row.rating}★ review` : "Verified customer",
  };
}

export default function TestimonialsSlider() {
  const [items, setItems] = useState(fallbackTestimonials);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let reviews = [];
      const res = await getSiteReviews(12);
      if (res.ok && Array.isArray(res.data?.reviews)) {
        reviews = res.data.reviews;
      }
      if (!reviews.length) {
        try {
          reviews = await fetchReviewsFromSheet({ limit: 12 });
        } catch {
          /* keep fallback */
        }
      }
      if (cancelled || !reviews.length) return;
      setItems(reviews.map(mapApiReview));
      setI(0);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (paused || items.length < 2) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % items.length), AUTO);
    return () => clearInterval(t);
  }, [paused, items.length]);

  const t = items[i] || items[0];

  if (!t) return null;

  return (
    <section
      className="lux-testimonials"
      aria-roledescription="carousel"
      aria-label="Customer testimonials"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="lux-section-head">
        <p className="lux-eyebrow">Stories</p>
        <h2 className="lux-h2">Kind words</h2>
      </div>

      <div className="lux-testimonials__viewport">
        <AnimatePresence mode="wait">
          <motion.article
            key={t.id}
            className="lux-testimonial-card"
            initial={{ opacity: 0, x: 28 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -28 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="lux-testimonial-card__quote">&ldquo;{t.quote}&rdquo;</p>
            <div className="lux-testimonial-card__person">
              {t.avatar ? (
                <img src={t.avatar} alt="" width={56} height={56} loading="lazy" />
              ) : (
                <span className="lux-testimonial-card__avatar" aria-hidden>
                  {t.name.charAt(0)}
                </span>
              )}
              <div>
                <p className="lux-testimonial-card__name">{t.name}</p>
                <p className="lux-testimonial-card__role">{t.role}</p>
              </div>
            </div>
          </motion.article>
        </AnimatePresence>

        {items.length > 1 && (
          <div className="lux-testimonials__dots">
            {items.map((row, idx) => (
              <button
                key={row.id}
                type="button"
                className={idx === i ? "is-active" : ""}
                aria-label={`Go to testimonial ${idx + 1}`}
                aria-current={idx === i}
                onClick={() => setI(idx)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
